import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { Plus, Pencil, Loader2, Search, Contact, History, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getCustomers, createCustomer, updateCustomer, getCustomerHistory } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function ClientsManagement() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null); // {id?, name, phone}
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await getCustomers());
    } catch {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await updateCustomer(form.id, { name: form.name, phone: form.phone });
        toast.success("Client updated");
      } else {
        await createCustomer({ name: form.name, phone: form.phone });
        toast.success("Client added");
      }
      setForm(null);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (c) => {
    setHistory({ name: c.name, phone: c.phone, sessions: null });
    setHistoryLoading(true);
    try {
      const data = await getCustomerHistory(c.id);
      setHistory(data);
    } catch {
      toast.error("Failed to load history");
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <Contact className="h-6 w-6" /> Clients
          </h2>
          <p className="text-sage text-sm">Customer database for bookings & marketing</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-forest/10 text-forest hover:bg-forest/10" data-testid="total-clients-badge">
            {clients.length} Registered {clients.length === 1 ? "Client" : "Clients"}
          </Badge>
          <Button className="bg-forest hover:bg-forest-light" onClick={() => setForm({ name: "", phone: "" })} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Client
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sage" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…" className="pl-10" data-testid="client-search" />
      </div>

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-sand">
                <TableHead className="table-header-cell w-12">No.</TableHead>
                <TableHead className="table-header-cell">Client Name</TableHead>
                <TableHead className="table-header-cell">Phone Number</TableHead>
                <TableHead className="table-header-cell">Total Visits</TableHead>
                <TableHead className="table-header-cell">Last Visit</TableHead>
                <TableHead className="table-header-cell w-40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-sage">No clients found.</TableCell></TableRow>
              ) : filtered.map((c, i) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell className="text-sage">{i + 1}</TableCell>
                  <TableCell className="font-medium text-forest">{c.name}</TableCell>
                  <TableCell className="text-sage">{c.phone || "-"}</TableCell>
                  <TableCell><Badge variant="outline" className="border-forest/20 text-forest">{c.visits}</Badge></TableCell>
                  <TableCell className="text-sage">{c.last_visit ? format(new Date(c.last_visit), "d MMM yyyy") : "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setForm({ id: c.id, name: c.name, phone: c.phone || "" })} data-testid={`edit-client-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-forest" onClick={() => openHistory(c)} data-testid={`history-client-${c.id}`}>
                        <History className="h-3.5 w-3.5 mr-1" /> History
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-md" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">{form?.id ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription>{form?.id ? "Update this client's profile" : "Create a new client record"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Client Name *</Label>
              <Input value={form?.name || ""} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid="client-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Phone Number</Label>
              <Input value={form?.phone || ""} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Optional" data-testid="client-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-forest hover:bg-forest-light" onClick={handleSave} disabled={saving} data-testid="save-client-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form?.id ? "Save Changes" : "Create Client")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View History dialog */}
      <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest flex items-center gap-2"><History className="h-5 w-5" /> {history?.name}</DialogTitle>
            <DialogDescription className="flex items-center gap-1">
              {history?.phone ? (<><Phone className="h-3.5 w-3.5" /> {history.phone}</>) : "No phone on file"}
            </DialogDescription>
          </DialogHeader>
          {historyLoading || !history?.sessions ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
          ) : history.sessions.length === 0 ? (
            <p className="text-sm text-sage py-8 text-center">No booking history yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-sand">
                  <TableHead className="table-header-cell">Date</TableHead>
                  <TableHead className="table-header-cell">Therapist</TableHead>
                  <TableHead className="table-header-cell">Service</TableHead>
                  <TableHead className="table-header-cell text-right">Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.sessions.map((s) => (
                  <TableRow key={s.id} data-testid={`history-session-${s.id}`}>
                    <TableCell className="text-sage">{format(new Date(s.date), "d MMM yyyy")}</TableCell>
                    <TableCell className="text-forest">{s.therapist_name}</TableCell>
                    <TableCell className="text-forest">{s.service_name}{s.duration ? ` · ${s.duration}m` : ""}</TableCell>
                    <TableCell className="text-right font-medium text-forest">{money(s.price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
